/*
 * overview.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Chip,
  Collapse,
  Grid2,
  Backdrop,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import {
  ExpandMore,
  ExpandLess,
  OpenInNew,
} from "@mui/icons-material";
import Modal from "@mui/material/Modal";

import Moment from "moment";

import Topheader from "../../../Layout/header/header";
import APIManager from "../../../Services/ApiManager/ApiManager";
import PopupDialog from "../../../Components/PopupDialog";
import ApplicationQaReportsPopUp from "../../../Components/ApplicationQaReportsPopUp/ApplicationQaReportsPopUp";
import "./global-admin-overview.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function GlobalAdminOverview() {
  const [versionOpen, setVersionOpen] = useState(true);
  const params = useParams();
  const [appOverviewData, setAppOverviewData] = useState();
  const navigate = useNavigate();
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [title, setTitle] = useState("");
  const [versionName, setVersionName] = useState("");
  const [appIcon, setAppIcon] = useState("");
  const [category, setCategory] = useState("");
  const [isOemApp] = useState(false);
  const [publishedBy, setPublishedBy] = useState("");
  const [publisherTeam, setPublisherTeam] = useState("");
  const [teamDetails, setTeamDetails] = useState("");
  const [showQAReports, setShowQAReports] = useState([]);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [targetedMakes, setTargetedMakes] = useState([]);
  const [visibleMakes, setVisibleMakes] = useState([]);
  const [targetedCountries, setTargetedCountries] = useState([]);
  const [visibleCountries, setVisibleCountries] = useState([]);
  const [targetedECUTypes, setTargetedECUTypes] = useState([]);
  const [visibleECUTypes, setVisibleECUTypes] = useState([]);
  const [targetedLanguages, setTargetedLanguages] = useState([]);
  const [targetedCPUTypes, setTargetedCPUTypes] = useState([]);
  const [visibleLanguages, setVisibleLanguages] = useState([]);
  const [visibleCPUTypes, setVisibleCPUTypes] = useState([]);
  const [qaReports, setQaReports] = useState([]);
  const [showAllQaReportsSection, setShowAllQaReportsSection] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloadsCount] = useState(0);
  const [sessionsCount] = useState(0);
  const [activeVehiclesCount] = useState(0);
  const hasRun = useRef(false);
  const [apiLevel, setApiLevel] = useState();
  const [status, setStatus] = useState();
  const [lastModifiedDate, setLastModifiedDate] = useState();
  const [rejectedDate, setRejectedDate] = useState();
  const [approvedDate, setApprovedDate] = useState();
  const [submittedDate, setSubmittedDate] = useState();
  const [publishDate, setPublishDate] = useState();
  const [downloads, setDownloads] = useState(0);
  const [sessions, setSessions] = useState(0);
  const [activeVehicles, setActiveVehicles] = useState(0);
  const [showAllMakes, setShowAllMakes] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [showAllECUTypes, setShowAllECUTypes] = useState(false);
  const [showAllCPUTypes, setShowAllCPUTypes] = useState(false);
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [id, setId] = useState();
  const [submissionId, setSubmissionId] = useState();

  const handleBackClick = () => {
    navigate("/ui/harman-admin/dashboard");
  };

  const handleShowAllCountries = (showAll) => {
    setShowAllCountries(showAll);
    setVisibleCountries(
      showAll ? targetedCountries : targetedCountries.slice(0, 14)
    );
  };

  const handleShowAllMakes = (showAll) => {
    setShowAllMakes(showAll);
    setVisibleMakes(showAll ? targetedMakes : targetedMakes.slice(0, 14));
  };

  const handleShowAllECUTypes = (showAll) => {
    setShowAllECUTypes(showAll);
    setVisibleECUTypes(
      showAll ? targetedECUTypes : targetedECUTypes.slice(0, 14)
    );
  };

  const handleShowAllCPUTypes = (showAll) => {
    setShowAllCPUTypes(showAll);
    setVisibleCPUTypes(
      showAll ? targetedCPUTypes : targetedCPUTypes.slice(0, 14)
    );
  };

  const handleShowAllLanguages = (showAll) => {
    setShowAllLanguages(showAll);
    setVisibleLanguages(
      showAll ? targetedLanguages : targetedLanguages.slice(0, 14)
    );
  };
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setShowAllQaReportsSection(!showAllQaReportsSection);
    const showTwoQAReports = qaReports.slice(0, 2);
    setShowQAReports(showTwoQAReports);
  };

  const handleViewMoreReports = (clickShowReportsToggleBoolean) => {
    setShowAllQaReportsSection(clickShowReportsToggleBoolean);
    if (clickShowReportsToggleBoolean) {
      handleOpen();
      const showFullQAReports = qaReports;
      setShowQAReports(showFullQAReports);
    } else {
      const showTwoQAReports = qaReports.slice(0, 2);
      setShowQAReports(showTwoQAReports);
    }
  };

  const expandedReports = (id, version, submissionId) => {
    setLoader(true);
    setSubmissionId(submissionId);
    setId(id);
    APIManager.getGlobalAppBasedOnVersionCode(id, submissionId)
      .then((response) => {
        setAppOverviewData(response);
        const index = response.submissions.findIndex(
          (x) => x.name === version
        );
        responseSettingOverviewData(index, response);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error"
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }

  const handleVersionToggle = (version, submissionId) => {
    setVersionOpen(!versionOpen);
    setExpandedVersion(expandedVersion === version ? null : version);
    if (expandedVersion !== version) {
      if (appOverviewData) {
        expandedReports(appOverviewData.id, version, submissionId);
      }
    }
  }

  const setTargetingData = (targetingKey, setter, visibleSetter, showAllFlag) => {
    const data = targetingKey || ["N/A"];
    setter(data);
    if (data.length > 0 && data[0] !== "N/A") {
      visibleSetter(showAllFlag ? data : data.slice(0, 14));
    }
  }

  function responseSettingOverviewData(index, response) {
    const submission = response.submissions[index];
    const targeting = submission?.targeting || {};

    setExpandedVersion(submission.name);
    setTitle(response.name || "N/A");
    setStatus(submission.status || "N/A");
    setVersionName(submission?.name);
    setAppIcon(response.iconUrl || "N/A");
    setCategory(response.categoryName || "N/A");
    setLastModifiedDate(submission?.lastModifiedAt);
    setRejectedDate(submission?.rejectedAt);
    setApprovedDate(submission?.approvedAt);
    setSubmittedDate(submission?.submittedAt);
    setPublishDate(submission?.publishedAt);
    setApiLevel(submission?.apiLevel || "N/A");
    setQaReports(submission?.reports || ["N/A"]);

    setTargetingData(targeting?.makes, setTargetedMakes, setVisibleMakes, showAllMakes);
    setTargetingData(targeting?.countries, setTargetedCountries, setVisibleCountries, showAllCountries);
    setTargetingData(targeting?.ecuTypes, setTargetedECUTypes, setVisibleECUTypes, showAllECUTypes);
    setTargetingData(targeting?.cpuTypes, setTargetedCPUTypes, setVisibleCPUTypes, showAllCPUTypes);
    setTargetingData(submission?.languages, setTargetedLanguages, setVisibleLanguages, showAllLanguages);

    setLoader(false);
  }

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    setLoader(true);
    APIManager.getGlobalAdminAppOverviewData(params.contentID)
      .then((response) => {
        setAppOverviewData(response);
        setPublishedBy(response?.publisherTeamDetails?.managers[0]?.email || "N/A");
        setPublisherTeam(response?.publisherTeamDetails?.managers[0]?.publisherDetails || "");
        setTeamDetails(response?.publisherTeamDetails?.managers[0]?.name || "N/A");
        expandedReports(response.id, response.submissions[0].name, response.submissions[0].id);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error"
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });

    APIManager.getGlobalAdminAnalyticsData(params.packageName)
      .then((response) => {
        setDownloads(response?.totalInstalls?.currentCount || "0");
        setSessions(response?.activeSessions?.currentCount || "0");
        setActiveVehicles(response?.activeDevices?.currentCount || "0");
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error"
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    if (qaReports.length > 0) {
      const showTwoQAReports = qaReports.slice(0, 2);
      setShowQAReports(showTwoQAReports);
    }
  }, [qaReports]);

  const navigateToApplicationDetails = ($event, index) => {
    navigate(`/ui/harman-admin/application/${id}/submission/${submissionId}`);
  };

  const navigateToApplicationDetailsNewTab = ($event, index) => {
    $event.stopPropagation();
    const navigatingURL = `/ui/harman-admin/application/${id}/submission/${submissionId}`;

    if (index === 1) {
      globalThis.location.href = navigatingURL;
    } else {
      window.open(navigatingURL, "_blank");
    }
  };

  const renderStatisticsGrid = (data) => {
    const { items, spacing = 10 } = data;
    return (
      <Grid2 container spacing={spacing}>
        {items.map((item) => (
          <Grid2 size={4} key={item.label}>
            <Box textAlign="center">
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={item.fontWeight || 600}
              >
                {item.label}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {item.value}
              </Typography>
            </Box>
          </Grid2>
        ))}
      </Grid2>
    );
  };

  const renderStatisticsSection = (config) => {
    const {
      title,
      icon,
      items,
      showSection = false,
      gridSpacing = 10,
    } = config;

    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
        style={{ display: showSection ? "flex" : "none" }}
      >
        <Box sx={{ width: "50%", pr: 1 }}>
          <Stack direction="row" alignItems="center" mb={2}>
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: "secondary.main",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
              }}
            >
              <Box sx={{ display: "none" }}>
                {icon}
              </Box>
            </Box>
            <Typography variant="subtitle1">{title}</Typography>
          </Stack>

          <Paper
            elevation={1}
            sx={{ bgcolor: "rgba(18, 20, 21, 0.05)", p: 2 }}
          >
            {renderStatisticsGrid({ items, spacing: gridSpacing })}
          </Paper>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {loader && (
        <Backdrop
          open={loader}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Topheader />
      {/* Header Navigation */}

      <Box
        sx={{
          paddingTop: "30px",
          paddingBottom: "40px",
          paddingLeft: "80px",
          paddingRight: "80px",
          display: "flex",
          justifyContent: "flex-start",
          marginTop: "3%",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            width: "96px",
            height: "36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
          onClick={handleBackClick}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.825 9L9.425 14.6L8 16L0 8L8 0L9.425 1.4L3.825 7H16V9H3.825Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          </Box>

          <Typography variant="body1" color="text.secondary">
            Dashboard
          </Typography>
        </Box>

        <Box
          sx={{
            marginLeft: "30px",
            marginRight: "30px",
            width: "1100px",
          }}
        >
          {/* App Information */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",

              backgroundColor: "rgba(255, 255, 255, 1)",
              gap: "18px",

              marginBottom: "20px",
              height: "80px",
            }}
          >
            <Box
              sx={{
                width: "80px",

                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <img
                src={appIcon}
                alt="App Icon"
                width="80px"
                height="80px"
                style={{ borderRadius: "8px" }}
              />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight={500}>
                {title || "N/A"}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "40px",
                  height: "20px",
                  marginTop: "6px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    width: "max-content",
                    color: "rgba(0, 86, 45, 1)",
                    backgroundColor: "rgba(152, 240, 200, 1)",
                    borderRadius: "4px",
                    padding: "5px 8px 5px 8px",
                    fontWeight: "600",
                  }}
                >
                  {status}
                </Typography>{" "}
              </Box>
            </Box>
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 1, color: "text.primary" }}
          >
            Analytics
          </Typography>
          <Paper
            elevation={0}
            sx={{
              //mx: 3,
              mb: 2,
              backgroundColor: "#F3F3F3",
              borderRadius: "4px",
              height: "max-content",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                <Box
                  component="span"
                  color="rgba(90, 91, 92, 1)"
                  fontSize={"14px"}
                  fontWeight={600}
                >
                  No of Downloads:
                </Box>
                <Box component="span" ml={1} fontWeight={500}>
                  {downloads || "N/A"}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary" fontWeight={600}>
                  No of Sessions:
                </Box>
                <Box component="span" ml={1}>
                  {sessions || "N/A"}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary" fontWeight={600}>
                  No of Active Vehicles:
                </Box>
                <Box component="span" ml={1}>
                  {activeVehicles || "N/A"}
                </Box>
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              style={{ display: "none" }}
            >
              <svg
                width="1152"
                height="1"
                viewBox="0 0 1152 1"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="0.5"
                  y1="0.5"
                  x2="1151.5"
                  y2="0.5"
                  stroke="#121415"
                  strokeOpacity="0.2"
                  strokeLinecap="round"
                  strokeDasharray="4 4"
                />
              </svg>
            </Box>
            {/* Statistics and Payments */}
            {renderStatisticsSection({
              title: "Statistics",
              items: [
                { label: "No of Downloads", value: downloadsCount, fontWeight: 600 },
                { label: "No of Sessions", value: sessionsCount, fontWeight: 600 },
                { label: "No of Active Vehicles", value: activeVehiclesCount, fontWeight: 600 },
              ],
              showSection: false,
            })}
          </Paper>

          {/* Main Content */}
          <Paper
            elevation={0}
            sx={{
              // mx: 3,
              mb: 2,
              backgroundColor: "#F3F3F3",
              borderRadius: "4px",
              height: "max-content",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                <Box
                  component="span"
                  color="rgba(90, 91, 92, 1)"
                  fontSize={"14px"}
                  fontWeight={600}
                >
                  Category:
                </Box>
                <Box component="span" ml={1} fontWeight={500}>
                  {category || "N/A"}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary" fontWeight={600}>
                  Published by:
                </Box>
                <Box component="span" ml={1}>
                  {publishedBy && publishedBy.length > 10 ? (
                    <Tooltip title={publishedBy} arrow placement="top">
                      <span>
                        {publishedBy.substring(0, 10)}...
                      </span>
                    </Tooltip>
                  ) : (
                    publishedBy
                  )}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary" fontWeight={600}>
                  Publisher Team:
                </Box>
                <Box component="span" ml={1}>
                  {publisherTeam}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary" fontWeight={600}>
                  Team Details:
                </Box>
                <Box component="span" ml={1}>
                  {teamDetails}
                </Box>
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              style={{ display: "none" }}
            >
              <svg
                width="1152"
                height="1"
                viewBox="0 0 1152 1"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="0.5"
                  y1="0.5"
                  x2="1151.5"
                  y2="0.5"
                  stroke="#121415"
                  strokeOpacity="0.2"
                  strokeLinecap="round"
                  strokeDasharray="4 4"
                />
              </svg>
            </Box>
            {/* Statistics and Payments */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
              }}
              style={{ display: "none" }}
            >
              <Box sx={{ width: "50%", pr: 1 }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "secondary.main",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <Box sx={{ display: "none" }}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0 18V16L2 14V18H0ZM4 18V12L6 10V18H4ZM8 18V10L10 12.025V18H8ZM12 18V12.025L14 10.025V18H12ZM16 18V8L18 6V18H16ZM0 12.825V10L7 3L11 7L18 0V2.825L11 9.825L7 5.825L0 12.825Z"
                          fill="white"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Typography variant="subtitle1">Statistics</Typography>
                </Stack>

                <Paper
                  elevation={1}
                  sx={{ bgcolor: "rgba(18, 20, 21, 0.05)", p: 2 }}
                >
                  {renderStatisticsGrid({
                    items: [
                      { label: "No of Downloads", value: downloadsCount },
                      { label: "No of Sessions", value: sessionsCount },
                      { label: "No of Active Vehicles", value: activeVehiclesCount },
                    ],
                  })}
                </Paper>
              </Box>

              <Box sx={{ width: "50%", pr: 1 }} style={{ display: "none" }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "secondary.main",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <Box sx={{}}>
                      <svg
                        width="20"
                        height="16"
                        viewBox="0 0 20 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM2 8H18V4H2V8Z"
                          fill="white"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Typography variant="subtitle1">Statistics</Typography>
                </Stack>

                <Paper
                  elevation={1}
                  sx={{ bgcolor: "rgba(18, 20, 21, 0.05)", p: 2 }}
                >
                  {renderStatisticsGrid({
                    items: [
                      { label: "Total Revenue", value: "12" },
                      { label: "Revenue from last 30 days", value: "12" },
                      { label: "Products Offered", value: "123" },
                    ],
                  })}
                </Paper>
              </Box>
            </Box>
          </Paper>
          {/* Version Section */}
          {appOverviewData?.submissions.map((appVersionData) => (
            <Box
              key={appVersionData.id}
              sx={{
                borderRadius: "4px",
                border: "1px solid",
                marginBottom: "16px",
                borderColor: "rgba(18, 20, 21, 0.2)",
              }}
            >
              <Box
                className="version-section"
                sx={{
                  mx: 2,
                  py: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: "32px",
                }}
                onClick={() =>
                  handleVersionToggle(
                    appVersionData.name,
                    appVersionData.id
                  )
                }
              >
                <Box
                  direction="row"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  width={"25%"}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ textDecoration: "underline" }}
                    onClick={($event) =>
                      navigateToApplicationDetails($event, 1)
                    }
                  >
                    {appVersionData?.name}
                  </Typography>

                  <OpenInNew
                    fontSize="small"
                    color="action"
                    onClick={($event) =>
                      navigateToApplicationDetailsNewTab($event, 2)
                    }
                  />
                </Box>
                {expandedVersion !== appVersionData.name && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "25%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          width: "max-content",
                          color: "rgba(0, 86, 45, 1))",
                          backgroundColor: "rgba(152, 240, 200, 1)",
                          borderRadius: "4px",
                          padding: "5px 8px 5px 8px",
                          fontWeight: "600",
                        }}
                      >
                        {appVersionData?.status}
                      </Typography>
                    </Box>

                    <Box
                      direction="row"
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      width={"25%"}
                      gap={1}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Last Modified Date:
                      </Typography>
                      <Typography>
                        {Moment(lastModifiedDate).format(
                          "DD MMM YYYY"
                        )}
                      </Typography>
                    </Box>
                  </>
                )}
                <Box>
                  <IconButton size="small">
                    {expandedVersion === appVersionData.name ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedVersion === appVersionData.name}>
                <Box className="qaReports-section">
                  <Box
                    sx={{
                      mx: 2,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "2px",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Reports
                    </Typography>
                    {qaReports.length > 2 && (
                      <Typography
                        sx={{
                          fontWeight: "500",
                          fontSize: "12px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          color: "#0476E2 !important",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          handleViewMoreReports(!showAllQaReportsSection);
                          handleOpen();
                        }}
                      >
                        View More Reports
                      </Typography>
                    )}
                  </Box>
                  {qaReports && qaReports.length > 0 && qaReports[0] !== "N/A" && (
                    <Box
                      sx={{
                        //mx: 2,
                        mb: 2,
                      }}
                    >
                      {!showAllQaReportsSection && (
                        <ApplicationQaReportsPopUp qaReports={showQAReports} />
                      )}
                    </Box>
                  )}
                </Box>
                
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexWrap={"wrap"}
                  mb={2}
                  mx={2}
                  marginTop={1}
                >
                  <Box width={"25%"} mb={2}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Status
                    </Typography>

                    <Typography
                      variant="body2">

                      {appVersionData?.status
                        ? appVersionData.status.charAt(0).toUpperCase() +
                        appVersionData.status.slice(1).toLowerCase()
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box width={"25%"} mb={2}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      API
                    </Typography>
                    <Typography variant="body2">
                      {apiLevel ||
                        "N/A"}
                    </Typography>
                  </Box>

                  <Box width={"25%"} mb={2}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Last Modified Date
                    </Typography>
                    <Typography variant="body2">
                      {" "}
                      {lastModifiedDate ? Moment(lastModifiedDate).format(
                        "DD MMM YYYY"
                      ) : ""}
                    </Typography>
                  </Box>

                  <Box width={"25%"} mb={1}>
                  </Box>

                  <Box width={"25%"} mb={1}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Submitted Date
                    </Typography>
                    <Typography variant="body2">
                      {" "}
                      {submittedDate ? Moment(submittedDate).format(
                        "DD MMM YYYY"
                      ) : ""}
                    </Typography>
                  </Box>

                  <Box width={"25%"} mb={1}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Approved Date
                    </Typography>
                    <Typography variant="body2">
                      {" "}
                      {approvedDate ? Moment(approvedDate).format(
                        "DD MMM YYYY"
                      ) : ""}
                    </Typography>
                  </Box>

                  <Box width={"25%"} mb={1}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Rejected Date
                    </Typography>
                    <Typography variant="body2">
                      {" "}
                      {rejectedDate ? Moment(rejectedDate).format(
                        "DD MMM YYYY"
                      ) : ""}
                    </Typography>
                  </Box>

                  <Box width={"25%"} mb={1}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Published Date
                    </Typography>
                    <Typography variant="body2">
                      {" "}
                      {publishDate ? Moment(publishDate).format("DD MMM YYYY") : ""}
                    </Typography>
                  </Box>
                </Box>

                <Box className="targeted-section">
                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Targeted Makes
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleMakes.map((make) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={make}
                          label={make}
                          size="small"
                        />
                      ))}
                      {""}
                      {!showAllMakes && targetedMakes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllMakes(true)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {targetedMakes.length - 14} {" More"} {" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {" "}
                            <path
                              d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
                              fill="#0476E2"
                            />{" "}
                          </svg>
                        </Button>
                      )}
                      {showAllMakes && targetedMakes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllMakes(false)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {"Collapse"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
                              fill="#0476E2"
                            />
                          </svg>
                        </Button>
                      )}{" "}
                    </Stack>
                  </Box>

                  <Box mb={3}>
                    {" "}
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Targeted Countries{" "}
                    </Typography>{" "}
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {" "}
                      {visibleCountries.map((country) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={country}
                          label={country}
                          size="small"
                        />
                      ))}{" "}
                      {!showAllCountries && targetedCountries.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllCountries(true)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {targetedCountries.length - 14} {" More"}
                          {""}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {" "}
                            <path
                              d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
                              fill="#0476E2"
                            />{" "}
                          </svg>
                        </Button>
                      )}
                      {showAllCountries && targetedCountries.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllCountries(false)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {"Collapse"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
                              fill="#0476E2"
                            />
                          </svg>
                        </Button>
                      )}{" "}
                    </Stack>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      ECU Types
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleECUTypes.map((type) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={type}
                          label={type}
                          size="small"
                        />
                      ))}{" "}
                      {!showAllECUTypes && targetedECUTypes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllECUTypes(true)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {targetedECUTypes.length - 14} {" More"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {" "}
                            <path
                              d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
                              fill="#0476E2"
                            />{" "}
                          </svg>
                        </Button>
                      )}
                      {showAllECUTypes && targetedECUTypes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllECUTypes(false)}
                          style={{
                            height: "24px",
                            width: "100px",
                            textTransform: "none",
                            color: "rgba(4, 118, 226, 1)",
                            border: "1px solid rgba(4, 118, 226, 1)",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          {"Collapse"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
                              fill="#0476E2"
                            />
                          </svg>
                        </Button>
                      )}{" "}
                    </Stack>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      CPU Types
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleCPUTypes.map((type) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={type}
                          label={type}
                          size="small"
                        />
                      ))}{" "}
                      {!showAllCPUTypes && targetedCPUTypes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllCPUTypes(true)}
                        >
                          {targetedCPUTypes.length - 14} {" More"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {" "}
                            <path
                              d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
                              fill="#0476E2"
                            />{" "}
                          </svg>
                        </Button>
                      )}
                      {showAllCPUTypes && targetedCPUTypes.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllCPUTypes(false)}
                        >
                          {"Collapse"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
                              fill="#0476E2"
                            />
                          </svg>
                        </Button>
                      )}{" "}
                    </Stack>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Language
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleLanguages.map((language) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={language}
                          label={language}
                          size="small"
                        />
                      ))}{" "}
                      {!showAllLanguages && targetedLanguages.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllLanguages(true)}
                        >
                          {targetedLanguages.length - 14} {" More"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {" "}
                            <path
                              d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
                              fill="#0476E2"
                            />{" "}
                          </svg>
                        </Button>
                      )}
                      {showAllLanguages && targetedLanguages.length > 14 && (
                        <Button
                          className="customButton"
                          size="small"
                          onClick={() => handleShowAllLanguages(false)}
                        >
                          {"Collapse"}{" "}
                          <svg
                            width="8"
                            height="10"
                            viewBox="0 0 8 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
                              fill="#0476E2"
                            />
                          </svg>
                        </Button>
                      )}{" "}
                    </Stack>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Box>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className="qaReportPopUp" sx={{ width: "900px", height: "444px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              fontWeight={"600"}
              marginLeft={"13px"}
            >
              QA Report
            </Typography>
            <button
              style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
              onClick={handleClose}
            >
              <IconButton component="span">
                {" "}
                <CloseIcon />{" "}
              </IconButton>
            </button>
          </div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",

              backgroundColor: "rgba(255, 255, 255, 1)",
              gap: "18px",
              marginLeft: "13px",
              marginBottom: "20px",
              height: "80px",
            }}
          >
            <Box
              sx={{
                width: "80px",

                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <img
                src={appIcon}
                alt="App Icon"
                width="80px"
                height="80px"
                style={{ borderRadius: "8px" }}
              />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight={550}>
                {title || "N/A"}
              </Typography>
              <Box sx={{ display: "flex", gap: "6px" }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {versionName}
                </Typography>
                {isOemApp === true ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "40px",
                      height: "20px",
                      marginTop: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "600px",
                        color: "#2A2C2D",
                        backgroundColor: "#E8E8E8",
                        padding: "2px 4px 2px 4px",
                        borderRadius: "4px",
                      }}
                    >
                      OEM
                    </Typography>{" "}
                  </Box>
                ) : (
                  ""
                )}
              </Box>
            </Box>
          </Box>
          <div
            className="qaReports-popup-section"
            style={{ height: "310px", overflowY: "auto" }}
          >
            <ApplicationQaReportsPopUp qaReports={qaReports} />
          </div>
        </Box>
      </Modal>
    </>
  );
}

